import type {
  DiaryDayBody,
  DiaryDayEntry,
  DiaryDayGoalSnapshot,
  DiaryDayImportedTotals,
  DiaryDayPlanGeneration,
  DiaryExternalSource,
  DiaryExternalSourceSuppression,
  DiaryMeal,
  DiaryMealCustom,
  DiaryMealExercise,
  DiaryMealPeriodization,
  DiaryMealPlanKind,
  DiaryMealPlanPlannerResult,
  DiaryMealTarget,
  DiaryWorkoutActuals,
  DiaryWorkoutSupportSnack,
  DiaryWorkoutSupportSnackSuppression,
} from '../../validators/diary-day';
import {
  isMealCustomized,
  resolveMealPlannedAt,
  resolveMealTitle,
} from '../../validators/diary-day';

// ===========================================================================
// v1 (pre-`meals`) diary body shapes.
//
// These describe the LEGACY body only so the v1 -> v2 transform can read it.
// They intentionally live here (not exported) rather than in diary-day.ts so
// the canonical schema stays free of the retired shape.
// ===========================================================================

interface V1MealTarget {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

interface V1Exercise {
  durationMinutes?: number | null;
  intensity: 'low' | 'medium' | 'high' | null;
  caloriesBurned: number | null;
  carbsPerHour?: number | null;
  carbsConsumed: number | null;
  caloriesConsumed: number | null;
}

interface V1Locks {
  title: boolean;
  plannedAt: boolean;
  target: boolean;
  periodization: boolean;
  exercise: boolean;
}

interface V1UserOverrides {
  plannedAt?: string;
  target?: Partial<V1MealTarget>;
  exercise?: Partial<V1Exercise>;
}

interface V1Periodization {
  strategy: 'equal';
  mode: 'simple' | 'detail';
  simpleCarbLevel: 'low' | 'medium' | 'high' | null;
  generatedWeights: V1MealTarget;
  macroRatios: V1MealTarget;
  locks: V1Locks;
  userOverrides?: V1UserOverrides;
}

interface V1MealPlan {
  id: string;
  externalSource?: DiaryExternalSource;
  kind?: DiaryMealPlanKind;
  plannedAt: string;
  mealId: number | null;
  title: string;
  target: V1MealTarget;
  exercise: V1Exercise | null;
  workoutActuals?: DiaryWorkoutActuals | null;
  workoutSupportSnack?: DiaryWorkoutSupportSnack | null;
  workoutSupportSnackSuppression?: DiaryWorkoutSupportSnackSuppression;
  plannerResult?: DiaryMealPlanPlannerResult | null;
  periodization?: V1Periodization;
  completedAt?: string | null;
  skippedAt?: string | null;
  customizedAt?: string | null;
  deletedAt?: string | null;
}

interface V1Entry {
  id: string;
  externalSource?: DiaryExternalSource;
  loggedAt: string;
  mealId: number | null;
  mealPlanId?: string | null;
  serving: DiaryDayEntry['serving'];
  nutritionSnapshot: DiaryDayEntry['nutritionSnapshot'];
  source: DiaryDayEntry['source'];
  exercise?: DiaryDayEntry['exercise'];
  createdAt: string;
}

interface V1Body {
  goalSnapshot: DiaryDayGoalSnapshot | null;
  planOverride?: { target: V1MealTarget } | null;
  mealPlans?: V1MealPlan[];
  entries?: V1Entry[];
  externalSourceSuppressions?: DiaryExternalSourceSuppression[];
  importedTotals: DiaryDayImportedTotals | null;
  planGeneration?: DiaryDayPlanGeneration;
}

const UNSORTED_MEAL_ID = 'unsorted' as const;

const defaultPlanGeneration = (): DiaryDayPlanGeneration => ({
  generatedForDay: false,
  generatedAt: null,
  lastInputHash: null,
  status: 'idle',
  errorMessage: null,
  plannerDiagnostics: null,
});

/**
 * True when `body` is a legacy (pre-`meals`) diary body that still needs the
 * v1 -> v2 transform. A v2 body always carries a `meals` array.
 */
export const isLegacyDiaryDayBody = (body: unknown): body is V1Body => {
  if (!body || typeof body !== 'object') return false;
  const b = body as Record<string, unknown>;
  if (Array.isArray(b.meals)) return false;
  return Array.isArray(b.mealPlans) || Array.isArray(b.entries) || 'planOverride' in b;
};

const onlyDefined = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  const out: Partial<T> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) (out as Record<string, unknown>)[key] = value;
  }
  return out;
};

/**
 * Fold a legacy plan's `periodization.userOverrides` into the new partial
 * `custom` object. Only explicit user overrides carry over — completion
 * `locks` are NOT converted, because a completion lock merely froze a value
 * that already lives in `planned` (and is protected by `completedAt`), whereas
 * `custom` represents an authoritative user choice the planner must honor.
 */
const buildCustom = (plan: V1MealPlan): DiaryMealCustom | undefined => {
  const overrides = plan.periodization?.userOverrides;
  if (!overrides) return undefined;

  const custom: DiaryMealCustom = {};

  if (overrides.plannedAt !== undefined) {
    custom.plannedAt = overrides.plannedAt;
  }

  const overrideTarget = overrides.target ? onlyDefined(overrides.target) : {};
  if (Object.keys(overrideTarget).length > 0) {
    custom.target = overrideTarget as DiaryMealCustom['target'];
  }

  const overrideExercise = overrides.exercise ? onlyDefined(overrides.exercise) : {};
  if (Object.keys(overrideExercise).length > 0) {
    custom.exercise = overrideExercise as DiaryMealCustom['exercise'];
  }

  return Object.keys(custom).length > 0 ? custom : undefined;
};

const stripEntry = (entry: V1Entry): DiaryDayEntry => {
  const { mealId: _mealId, mealPlanId: _mealPlanId, ...rest } = entry;
  return rest as DiaryDayEntry;
};

// A hand-logged exercise entry (not linked to a plan and not externally
// imported) becomes a first-class workout meal. This preserves the legacy
// backfill behavior that promoted such entries into workout plans.
const isLegacyHandLoggedExercise = (entry: V1Entry): boolean =>
  entry.source.type === 'exercise' && !entry.mealPlanId && !entry.externalSource;

const exerciseEntryToWorkoutMeal = (entry: V1Entry): DiaryMeal => {
  const burned = entry.nutritionSnapshot.calories ?? 0;
  const duration = entry.exercise?.durationMinutes ?? entry.serving.amount ?? 0;
  const intensity = entry.exercise?.intensity ?? null;
  const completedAt = entry.loggedAt;
  return {
    id: `workout-${entry.id}`,
    kind: 'workout',
    mealId: null,
    planned: {
      title: entry.source.label || 'Workout',
      plannedAt: entry.loggedAt,
      target: { calories: 0, protein: 0, carbs: 0, fat: 0 },
      exercise: {
        durationMinutes: duration,
        intensity,
        caloriesBurned: burned,
        carbsPerHour: null,
        carbsConsumed: null,
        caloriesConsumed: null,
      },
    },
    workoutActuals: {
      durationMinutes: duration,
      intensity,
      caloriesBurned: burned,
      fuelCarbsPerHour: null,
      fuelCalories: 0,
      fuelCarbs: 0,
      completedAt,
    },
    completedAt,
    skippedAt: null,
    entries: [stripEntry(entry)],
  };
};

const planToMeal = (plan: V1MealPlan): DiaryMeal => {
  const meal: DiaryMeal = {
    id: plan.id,
    mealId: plan.mealId,
    planned: {
      title: plan.title,
      plannedAt: plan.plannedAt,
      target: (plan.target ?? null) as DiaryMealTarget | null,
      exercise: (plan.exercise ?? null) as DiaryMealExercise | null,
    },
    entries: [],
  };
  if (plan.externalSource !== undefined) meal.externalSource = plan.externalSource;
  if (plan.kind !== undefined) meal.kind = plan.kind;
  const custom = buildCustom(plan);
  if (custom) meal.custom = custom;
  if (plan.periodization) {
    const { locks: _locks, userOverrides: _userOverrides, ...rest } = plan.periodization;
    meal.periodization = rest as DiaryMealPeriodization;
  }
  if (plan.plannerResult !== undefined) meal.plannerResult = plan.plannerResult;
  if (plan.workoutActuals !== undefined) meal.workoutActuals = plan.workoutActuals;
  if (plan.workoutSupportSnack !== undefined) meal.workoutSupportSnack = plan.workoutSupportSnack;
  if (plan.workoutSupportSnackSuppression !== undefined) {
    meal.workoutSupportSnackSuppression = plan.workoutSupportSnackSuppression;
  }
  if (plan.completedAt !== undefined) meal.completedAt = plan.completedAt;
  if (plan.skippedAt !== undefined) meal.skippedAt = plan.skippedAt;
  if (plan.deletedAt !== undefined) meal.deletedAt = plan.deletedAt;
  return meal;
};

/**
 * Pure v1 -> v2 transform. Converts the legacy scattered body
 * (mealPlans + entries + planOverride + periodization overrides/locks) into the
 * meals-centered body. Idempotent-safe: a body that already looks like v2 is
 * returned unchanged.
 */
export const migrateDiaryDayBodyV1ToV2 = (input: unknown): DiaryDayBody => {
  if (!isLegacyDiaryDayBody(input)) {
    return input as DiaryDayBody;
  }
  const body = input;

  const meals: DiaryMeal[] = (body.mealPlans ?? []).map(planToMeal);

  // Route entries into meals. A legacy food entry can carry both a configured
  // `mealId` and a `mealPlanId`; when those disagree between configured meals,
  // keep the configured meal association because the v2 hierarchy becomes the
  // only stored association. Workout/ad hoc plan links still win because older
  // data may carry a default numeric `mealId` alongside those plan ids.
  const mealById = new Map<string, DiaryMeal>();
  for (const meal of meals) mealById.set(meal.id, meal);

  const findByConfigMeal = (configMealId: number | null): DiaryMeal | undefined => {
    if (configMealId == null) return undefined;
    return meals.find((m) => m.mealId === configMealId && !m.deletedAt);
  };

  // Synthetic meals created for unplanned entries, keyed by config mealId
  // (or 'unsorted' for entries with no meal at all).
  const bucketByMealId = new Map<number | 'unsorted', DiaryMeal>();
  const bucketFor = (mealId: number | null | undefined, loggedAt: string): DiaryMeal => {
    const key = mealId == null ? 'unsorted' : mealId;
    let bucket = bucketByMealId.get(key);
    if (!bucket) {
      bucket = {
        id: key === 'unsorted' ? UNSORTED_MEAL_ID : `meal-${key}`,
        mealId: key === 'unsorted' ? null : key,
        kind: 'meal',
        planned: {
          title: key === 'unsorted' ? 'Unsorted' : '',
          plannedAt: loggedAt,
          target: null,
          exercise: null,
        },
        entries: [],
      };
      bucketByMealId.set(key, bucket);
      meals.push(bucket);
    }
    return bucket;
  };

  for (const entry of body.entries ?? []) {
    if (isLegacyHandLoggedExercise(entry)) {
      meals.push(exerciseEntryToWorkoutMeal(entry));
      continue;
    }
    const byPlan = entry.mealPlanId ? mealById.get(entry.mealPlanId) : undefined;
    const byPlanWins =
      byPlan &&
      (entry.mealId == null || byPlan.mealId === entry.mealId || byPlan.mealId == null);
    const target = byPlanWins ? byPlan : findByConfigMeal(entry.mealId);
    if (target) {
      target.entries.push(stripEntry(entry));
    } else {
      // Keep the configured-meal association: bucket by mealId, not Unsorted.
      bucketFor(entry.mealId, entry.loggedAt).entries.push(stripEntry(entry));
    }
  }

  const result: DiaryDayBody = {
    goalSnapshot: body.goalSnapshot ?? null,
    meals,
    importedTotals: body.importedTotals ?? null,
    planGeneration: body.planGeneration ?? defaultPlanGeneration(),
  };
  if (body.externalSourceSuppressions !== undefined) {
    result.externalSourceSuppressions = body.externalSourceSuppressions;
  }
  return result;
};

/**
 * Rank a meal among same-id duplicates; higher wins.
 *
 * Mirrors the ranking `plansByExternalId` uses in the intervals.icu sync
 * service: a live copy beats a soft-deleted one, an authoritative user
 * customization beats a generated copy, and a copy the user has acted on (or
 * that holds logged food) beats an untouched one.
 */
const duplicateMealRank = (meal: DiaryMeal): number =>
  (meal.deletedAt ? 0 : 8)
  + (isMealCustomized(meal) ? 4 : 0)
  + (meal.completedAt || meal.skippedAt ? 2 : 0)
  + (meal.entries.length > 0 ? 1 : 0);

/**
 * Keep the winner's own fields, but never drop a logged entry that only exists
 * on the copy being collapsed away. (A soft-deleted meal is emptied of food
 * entries when it is deleted, so in practice this only rescues entries that a
 * divergent duplicate still holds.)
 */
const mergeDuplicateMeal = (winner: DiaryMeal, loser: DiaryMeal): DiaryMeal => {
  const seen = new Set(winner.entries.map((entry) => entry.id));
  const extra = loser.entries.filter((entry) => !seen.has(entry.id));
  if (extra.length === 0) return winner;
  return { ...winner, entries: [...winner.entries, ...extra] };
};

/**
 * Collapse meals that share an `id` down to one, at the position of the first
 * occurrence. Duplicate ids must never reach consumers: they disagree about
 * which copy to act on (`findIndex` updates the first, the delete path stamps
 * every match, the RxDB conflict handler keeps the last), so a single duplicate
 * lets an edit land on one copy while a delete hits another — or lets a stale
 * copy be resurrected.
 *
 * Returns the input array by reference when there is nothing to collapse, so
 * `normalizeDiaryDayBody` stays identity for well-formed bodies.
 */
const dedupeMealsById = (meals: DiaryMeal[]): DiaryMeal[] => {
  const slotById = new Map<string, number>();
  const result: DiaryMeal[] = [];
  let collapsed = false;

  for (const meal of meals) {
    const slot = slotById.get(meal.id);
    if (slot === undefined) {
      slotById.set(meal.id, result.length);
      result.push(meal);
      continue;
    }
    collapsed = true;
    const current = result[slot];
    // Ties keep the earlier copy, matching the first-match semantics most
    // consumers already use.
    const winnerIsNew = duplicateMealRank(meal) > duplicateMealRank(current);
    result[slot] = winnerIsNew
      ? mergeDuplicateMeal(meal, current)
      : mergeDuplicateMeal(current, meal);
  }

  return collapsed ? result : meals;
};

/**
 * Older clients generated a fresh id for every configured daily meal. Two
 * devices could consequently add separate empty copies of the same slot,
 * which a conflict merge by id correctly retained as independent additions.
 * Remove only empty copies: logged food, user choices and workout/sync state
 * must survive even when two meals occupy the same configured slot.
 */
const removeEmptyDuplicateConfiguredMeals = (meals: DiaryMeal[]): DiaryMeal[] => {
  const groups = new Map<string, DiaryMeal[]>();
  for (const meal of meals) {
    if (meal.deletedAt || meal.mealId == null || (meal.kind ?? 'meal') !== 'meal') continue;
    const key = JSON.stringify([
      meal.mealId,
      resolveMealPlannedAt(meal),
      resolveMealTitle(meal),
    ]);
    const group = groups.get(key);
    if (group) group.push(meal);
    else groups.set(key, [meal]);
  }

  const removedIds = new Set<string>();
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    const empties = group.filter((meal) =>
      meal.entries.length === 0
      && !meal.completedAt
      && !meal.skippedAt
      && !meal.custom
      && !meal.workoutActuals
      && !meal.externalSource
      && !meal.planned.exercise
      && !meal.workoutSupportSnack
      && !meal.workoutSupportSnackSuppression,
    );
    const removable = empties.length === group.length ? empties.slice(1) : empties;
    for (const meal of removable) removedIds.add(meal.id);
  }
  return removedIds.size > 0 ? meals.filter((meal) => !removedIds.has(meal.id)) : meals;
};

const withDedupedMeals = (body: DiaryDayBody): DiaryDayBody => {
  const meals = removeEmptyDuplicateConfiguredMeals(dedupeMealsById(body.meals));
  return meals === body.meals ? body : { ...body, meals };
};

/**
 * Defensive read-time normalizer for any stored DiaryDay body.
 *
 * Guarantees the caller a v2 (meals-centered) body regardless of what shape the
 * value actually has: a legacy v1 body is migrated, and a body that is missing
 * its `meals` array — e.g. the `{}` payloads that migration
 * `0019_repair_diary_days_document_model` wrote for empty rows — gets an empty
 * `meals` array so readers never hit "body.meals is not iterable". A well-formed
 * v2 body is returned unchanged (identity), so this is safe to apply on every
 * read. Idempotent.
 *
 * It also collapses meals that share an `id` (see `dedupeMealsById`), so a
 * duplicate written by any producer can never reach the consumers that
 * disagree about which copy to act on.
 * Empty copies of the same configured meal slot with different ids are also
 * removed, while meaningful meals and intentional ad hoc meals are preserved.
 *
 * This is the single source of truth shared by the frontend read-time backfill
 * and the backend routes that consume stored bodies directly.
 */
export const normalizeDiaryDayBody = (body: unknown): DiaryDayBody => {
  if (isLegacyDiaryDayBody(body)) {
    return withDedupedMeals(migrateDiaryDayBodyV1ToV2(body));
  }
  if (
    !body
    || typeof body !== 'object'
    || !Array.isArray((body as { meals?: unknown }).meals)
  ) {
    return {
      ...(body && typeof body === 'object' ? (body as Record<string, unknown>) : {}),
      meals: [],
    } as unknown as DiaryDayBody;
  }
  return withDedupedMeals(body as DiaryDayBody);
};
